import type { RoleEnum, TrainerCapability } from '@zeus/index'
import type { LoaderFunctionArgs } from 'react-router-dom'
import type { ProtectedPath, RouteAccess } from './route-access'
import { redirect } from 'react-router-dom'
import { GQL_ME } from '~/gql/auth/auth'
import { client } from '~/helpers/apollo'
import { can } from '~/helpers/auth/can'
import { useAuth } from '~/helpers/store/auth'
import { homeOrgRedirect } from './orgRouting'
import { ROUTE_ACCESS } from './route-access'

export function getDefaultRoute(role: RoleEnum): string {
  switch (role) {
    case 'SuperAdmin' as RoleEnum:
      return '/back-office/organizations'
    // Admin comme formateur atterrissent sur l'accueil (#201) et non sur un
    // outil : arrivé le matin, on se demande ce qu'on a à faire, pas comment
    // interpréter un tableau. Le SuperAdmin en est exclu — opérateur
    // plateforme, il n'appartient à aucune organisation et n'a donc ni promo
    // ni parcours à suivre.
    case 'Admin' as RoleEnum:
    case 'Formateur' as RoleEnum:
      return '/back-office/dashboard'
    case 'User' as RoleEnum:
      return '/my-sessions'
    default:
      return '/'
  }
}

/**
 * Porteur connecté, avec ses capacités (#11) et le slug de son espace (#241),
 * lus ensemble. Relu sur `me` tant que les capacités manquent : une connexion
 * ou une impersonation pose le porteur sans elles, et un écran ouvert sur des
 * capacités inconnues masquerait tout.
 *
 * Si cette relecture échoue alors que le porteur est déjà connu, on le garde :
 * une coupure réseau ne doit pas déconnecter. Ses capacités restent inconnues,
 * donc rien de réglable ne s'ouvre, et le prochain écran les relira.
 */
async function loadSession(): Promise<RoleEnum | null> {
  const { payload, capabilities } = useAuth.getState()
  if (payload && capabilities) return payload.role as RoleEnum

  try {
    const { data } = await client.query({ query: GQL_ME, fetchPolicy: 'network-only' })
    if (!data?.me) return null

    const role = data.me.roles as RoleEnum
    useAuth.getState().setPayload({
      firstName: data.me.firstName,
      lastName: data.me.lastName,
      email: data.me.email,
      username: data.me.username,
      role,
      id: data.me.id,
      impersonatorId: data.me.impersonatorId,
    })
    useAuth.getState().setCapabilities(data.me.capabilities)
    useAuth.getState().setOrgSlug(data.me.orgSlug ?? null)
    return role
  } catch (_error) {
    return (payload?.role as RoleEnum | undefined) ?? null
  }
}

/**
 * Recharge la page sous un autre basename, en REMPLAÇANT l'entrée d'historique :
 * au premier chargement, le `redirect` de react-router ferait un
 * `location.assign`, et Retour ramènerait sur l'URL quittée, qui redirigerait
 * aussitôt. Rien n'a à se rendre d'ici au déchargement : la promesse ne se
 * résout pas.
 */
function reloadAt(url: string): Promise<never> {
  window.location.replace(url)
  return new Promise(() => {})
}

/**
 * Loader d'une route protégée : rôle requis, et pour un Formateur la capacité
 * qui ouvre l'écran (#11). Refusé, on retombe sur la route par défaut du rôle —
 * l'accueil, qui n'exige aucune capacité : pas de boucle possible.
 *
 * Avant tout, un porteur ouvert sous le slug d'une autre org est renvoyé sur la
 * même page sous le sien (#241) : c'est là que vivent ses droits.
 */
export function createProtectedLoader(allowedRoles: RoleEnum[], capability?: TrainerCapability) {
  return async ({ request }: LoaderFunctionArgs) => {
    if (allowedRoles.length === 0) {
      useAuth.getState().setLoading(false)
      return null
    }

    const role = await loadSession()
    if (!role) {
      useAuth.getState().clearAuth()
      return redirect(`/`)
    }

    const home = homeOrgRedirect(new URL(request.url), useAuth.getState().orgSlug)
    if (home) return reloadAt(home)

    if (!allowedRoles.includes(role)) return redirect(getDefaultRoute(role))
    if (capability && !can(role, useAuth.getState().capabilities, capability)) {
      return redirect(getDefaultRoute(role))
    }
    return null
  }
}

/** Le loader d'une route déclarée dans `ROUTE_ACCESS` : ses rôles, et sa capacité. */
export function routeLoader(path: ProtectedPath) {
  const { roles, capability }: RouteAccess = ROUTE_ACCESS[path]
  return createProtectedLoader(roles as RoleEnum[], capability as TrainerCapability | undefined)
}
